import type { UseQueryOptionsType } from './UseQueryOptionsType';
import type { AxiosError } from 'axios';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import authStore from '@stores/AuthStore';
import { useStore } from '@stores/RootStore';
import { KEYS, repository } from '@repositories/Repository';
import type { AuthTokenExchangeRequestDTO } from '@repositories/tenantSsoProviderRepository/tenantSsoProvider.types';
import type IUserRepository from '@repositories/userRepository/IUserRepository';
import { ProfileModel } from '@repositories/userRepository/models/ProfileModel';
import type {
  ChangePassword,
  PasswordInitialUpdateRq,
  ProfileUpdateRq,
  User,
  UserFilter,
  UserInviteRq,
  UserPasswordRecoveryDTO,
  UserSignInRq,
} from '@repositories/userRepository/user.types';
import { isLimitedTokenResponse } from '@utils/authUtils';
import { swQueryKey } from './useSoftware';
import { tenantMemberQueryKey } from './useTenant';
import { userGroupQueryKey } from './useUserGroup';

const userRepo = repository.get<IUserRepository>(KEYS.USER_REPOSITORY);

export const userQueryKey = {
  all: ['user'] as const,
  lists: () => [...userQueryKey.all, 'list'] as const,
  list: (filter?: UserFilter) => [...userQueryKey.lists(), filter] as const,
  details: () => [...userQueryKey.all, 'detail'] as const,
  detail: (id: string) => [...userQueryKey.details(), id] as const,
};

export const profileQueryKey = {
  detail: () => ['profile'] as const,
};

export const useGetUserProfile = (id: UserId, options?: UseQueryOptionsType<ProfileModel>) =>
  useQuery<ProfileModel, AxiosError>({
    queryKey: profileQueryKey.detail(),
    queryFn: async () => {
      const result = await userRepo.getProfile(id);
      return new ProfileModel(result);
    },
    ...options,
    enabled: !!id && (options?.enabled !== undefined ? options.enabled : true),
    cacheTime: 0,
  });

export const useUpdateUserProfile = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: ProfileUpdateRq & { id: string }) => {
      const result = await userRepo.updateProfile(data.id, {
        address: data.address,
        name: data.name,
        phone: data.phone,
        email: data.email,
        userGroupIdList: data.userGroupIdList,
        customRole: data.customRole,
      });
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: profileQueryKey.detail() });
      queryClient.invalidateQueries({ queryKey: tenantMemberQueryKey.lists() });
      queryClient.invalidateQueries({ queryKey: userGroupQueryKey.lists() });
    },
  });
};

export const useChangeUserPassword = () => {
  const queryClient = useQueryClient();

  return useMutation(
    async (data: ChangePassword) => {
      await userRepo.changePassword(data.userId, data.password);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: profileQueryKey.detail() });
      },
    },
  );
};

export const useSignIn = (isAdmin?: boolean) => {
  const queryClient = useQueryClient();
  const { tenantId } = useParams();

  return useMutation<User, AxiosError | Error, UserSignInRq>(async (rqData: UserSignInRq) => userRepo.signIn(rqData), {
    onSuccess: data => {
      const isLimited = isLimitedTokenResponse(data);

      if (isLimited) {
        authStore.loginWithLimitedToken(data.id, data.name);
        queryClient.invalidateQueries({ queryKey: swQueryKey.all });
        return;
      }

      // 풀 토큰: 기존 로직
      const success = authStore.login(data.id, data.name, data.additionalPermissions, tenantId, isAdmin ?? false);
      if (!success) {
        throw Error('INVALID_TENANT');
      }

      // 회사가 제품을 구독하는지 여부가 로그인 후에 값이 제대로 나옴 -> 로그인 후에 제품 쿼리키 invalidate하여 다시 조회하도록 처리
      queryClient.invalidateQueries({ queryKey: swQueryKey.all });
    },
    onError: () => {
      // mutate() 호출의 onError로 에러가 전달됨 (재throw 불필요)
    },
  });
};

export const useLogout = () => {
  const { authStore } = useStore();
  const queryClient = useQueryClient();

  const logout = () => {
    authStore.logout();
    queryClient.clear();
  };

  return { logout };
};

export const useInviteUserToTenant = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (rqData: UserInviteRq) => userRepo.inviteUserToTenant(rqData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantMemberQueryKey.lists() });
    },
  });
};

export const useResendInviteMail = () => {
  return useMutation({
    mutationFn: async (userId: string) => userRepo.inviteUserToTenant(undefined, userId),
  });
};

export const useInitialPassword = () => {
  return useMutation({
    mutationFn: async (rqData: PasswordInitialUpdateRq) => userRepo.initialPassword(rqData),
    cacheTime: 100,
  });
};

export const useSendPasswordRecoveryMail = () => {
  type MutationVariables = {
    rqData: UserPasswordRecoveryDTO;
  };

  return useMutation({
    mutationFn: async (variables: MutationVariables) => userRepo.recoveryPassword({ email: variables.rqData.email }),
    onError: (error: AxiosError | Error) => {
      throw error;
    },
  });
};

// 제한 토큰 → 풀 토큰 교환 (테넌트 선택 후 사용)
export const useExchangeToken = () => {
  return useMutation<User, AxiosError, AuthTokenExchangeRequestDTO>({
    mutationFn: (dto: AuthTokenExchangeRequestDTO) => userRepo.exchangeToken(dto),
  });
};
